"use client";

import React, { useState } from "react";
import * as Yup from "yup";
import { Form, Formik, ErrorMessage, FieldArray, Field } from "formik";
import {
  AsteriskIcon,
  BookText,
  Check,
  Frame,
  Info,
  Settings2,
  Users,
  X,
  Plus,
} from "lucide-react";
import {
  Camera,
  Shot,
  ShotPart,
  ShotCharacter,
} from "@/app/types/shot-breakdown.types";
import {
  FormikInput,
  FormikSelect,
  FormikTextarea,
  FormikMultiSelect,
} from "@/app/components/input/inputs";
import { uuid } from "@/app/types/screenplay-editor.types";
import {
  aperture_options,
  camera_angles,
  camera_movements,
  camera_supports,
  lens_options,
  shot_types,
  vfx_types,
  fps_options,
  depth_of_field_options,
  lighting_options,
  sound_options,
  shot_frame,
  editing_purpose,
} from "@/data/shot-data";
import { crowdAgeOption, crowdTypeOption } from "@/data/crowd-data";

const formSteps = [
  {
    id: 1,
    name: "Core Identification",
    icon: Info,
    fields: ["priority", "shot_type", "camera_name"],
  },
  {
    id: 2,
    name: "Optics & Mechanics",
    icon: Settings2,
    fields: [
      "camera_angle",
      "camera_movement",
      "camera_support",
      "lens_mm",
      "aperture",
      "fps",
    ],
  },
  {
    id: 3,
    name: "Visual Composition",
    icon: Frame,
    fields: ["subject", "duration_seconds", "framing", "depth_of_field"],
  },
  {
    id: 4,
    name: "Narrative Context",
    icon: BookText,
    fields: [
      "action_blocking",
      "emotional_purpose",
      "edit_purpose",
      "reason",
      "lighting",
      "sound",
      "vfx",
    ],
  },
  {
    id: 5,
    name: "Assets & Movie Casting",
    icon: Users,
    fields: ["props", "set_props", "vehicles", "costumes"],
  },
];

interface AddLyricalShotFormProps {
  userShotValue?: Shot;
  onSubmit: (values: Omit<Shot, "id" | "parts">) => void;
  cameras: Camera[];
  shots: Shot[];
  movieCharacters: ShotCharacter[];
  handleCloseModal: () => void;
  parts: ShotPart[];
}

const AddLyricalShotForm = ({
  userShotValue,
  onSubmit,
  cameras,
  shots,
  movieCharacters,
  handleCloseModal,
  parts,
}: AddLyricalShotFormProps) => {
  const [step, setStep] = useState(1);
  const [highestStep, setHighestStep] = useState(1);
  const [customCharacterName, setCustomCharacterName] = useState("");
  const [customCrowdGender, setCustomCrowdGender] = useState("");
  const [customCrowdAgeGroup, setCustomCrowdAgeGroup] = useState("");

  const isCustomDepth =
    userShotValue?.depth_of_field &&
    !depth_of_field_options.some(
      (o) => o.name === userShotValue.depth_of_field,
    );

  const initialShotValues: Omit<Shot, "id" | "parts"> & {
    custom_depth_of_field?: string;
  } = {
    shot_number: userShotValue?.shot_number || shots.length + 1,
    priority: "Essential",
    shot_type: "",
    framing: "",
    camera_angle: [],
    camera_movement: "",
    camera_support: "",
    lens_mm: "",
    lens_feel: "",
    aperture: "T2.8",
    fps: 24,
    depth_of_field: isCustomDepth
      ? "Other"
      : userShotValue?.depth_of_field || "",
    custom_depth_of_field: isCustomDepth ? userShotValue.depth_of_field : "",
    subject: "",
    action_blocking: "",
    emotional_purpose: "",
    lighting: [],
    sound: [],
    duration_seconds: 5,
    edit_purpose: "",
    reason: "",
    vfx: "None",
    camera_name: cameras.length > 0 ? cameras[0].name : "Camera A",
    props: [],
    set_props: [],
    vehicles: [],
    costumes: [],
    characters: [],
    crowd: [],
  };

  const ShotSchema = Yup.object().shape({
    priority: Yup.string().required("Required"),
    shot_type: Yup.string().required("Required"),
    camera_angle: Yup.array().min(1, "Required"),
    camera_movement: Yup.string().required("Required"),
    camera_support: Yup.string().required("Required"),
    lens_mm: Yup.string().required("Required"),
    aperture: Yup.string().required("Required"),
    subject: Yup.string().required("Required"),
    duration_seconds: Yup.number().min(0).required("Required"),
    camera_name: Yup.string().required("Required"),
  });

  return (
    <Formik
      initialValues={
        userShotValue
          ? {
              ...(userShotValue as any),
              depth_of_field: isCustomDepth
                ? "Other"
                : userShotValue.depth_of_field || "",
              custom_depth_of_field: isCustomDepth
                ? userShotValue.depth_of_field
                : "",
            }
          : initialShotValues
      }
      validationSchema={ShotSchema}
      onSubmit={(values, { setSubmitting }) => {
        const submitValues = { ...values } as any;
        if (submitValues.depth_of_field === "Other") {
          submitValues.depth_of_field =
            submitValues.custom_depth_of_field || "";
        }
        delete submitValues.custom_depth_of_field;
        onSubmit(submitValues);
        setSubmitting(false);
      }}
    >
      {({
        setFieldValue,
        isSubmitting,
        setTouched,
        validateForm,
        values,
        submitForm,
      }) => {
        const selectedCharacters: ShotCharacter[] = values.characters || [];

        const handleToggleCharacter = (char: ShotCharacter) => {
          const exists = selectedCharacters.find((c) => c.id === char.id);
          let nextChars: ShotCharacter[];
          if (exists) {
            nextChars = selectedCharacters.filter((c) => c.id !== char.id);
          } else {
            nextChars = [...selectedCharacters, char];
          }
          setFieldValue("characters", nextChars);
        };

        const handleAddCustomCharacter = () => {
          const trimmed = customCharacterName.trim().toUpperCase();
          if (!trimmed) return;

          const exists = selectedCharacters.find(
            (c) => c.name.toUpperCase() === trimmed,
          );
          if (!exists) {
            const newChar: ShotCharacter = {
              id: `custom-${uuid()}`,
              name: trimmed,
            };
            setFieldValue("characters", [...selectedCharacters, newChar]);
          }
          setCustomCharacterName("");
        };

        const handleAddCustomCrowd = () => {
          const trimmedName = customCharacterName.trim().toUpperCase();
          if (!trimmedName) return;

          const selectedCrowd = values.crowd || [];
          const exists = selectedCrowd.find(
            (c: any) => c.name.toUpperCase() === trimmedName,
          );
          if (!exists) {
            const newCrowdItem = {
              id: `custom-crowd-${uuid()}`,
              name: trimmedName,
              gender: customCrowdGender || "All",
              ageGroup: customCrowdAgeGroup || "All",
            };
            setFieldValue("crowd", [...selectedCrowd, newCrowdItem]);
          }
          setCustomCharacterName("");
          setCustomCrowdGender("");
          setCustomCrowdAgeGroup("");
        };

        const handleNext = async () => {
          const errors = await validateForm();
          const currentStepInfo = formSteps.find((s) => s.id === step);
          if (!currentStepInfo) return;

          const stepErrors = Object.keys(errors).filter((key) =>
            currentStepInfo.fields.includes(key),
          );

          if (stepErrors.length === 0) {
            setHighestStep((prev) => Math.max(prev, step + 1));
            if (step < formSteps.length) {
              setStep(step + 1);
            } else {
              await submitForm();
            }
          } else {
            const touchedFields = currentStepInfo.fields.reduce(
              (acc, field) => {
                acc[field] = true;
                return acc;
              },
              {} as Record<string, boolean>,
            );
            setTouched(touchedFields, true);
          }
        };

        const handleBack = () => {
          setStep((s) => Math.max(s - 1, 1));
        };

        return (
          <Form className="flex flex-col overflow-hidden h-[90vh] lg:h-[85vh] bg-white rounded-b-3xl shadow-[0_12px_40px_-12px_rgba(0,0,0,0.15),_0_0_0_1px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
              {/* Left Step Navigation Sidebar */}
              <div className="w-full lg:w-72 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 bg-white p-4 lg:p-8 flex flex-col z-10 relative">
                <h3 className="hidden lg:block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-6 px-2">
                  {userShotValue
                    ? "Update Lyrical Shot"
                    : "Create Lyrical Shot"}
                </h3>
                <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {formSteps.map((formStep) => {
                    const isCurrent = step === formStep.id;
                    const isAccessible = formStep.id <= highestStep;
                    const isCompleted = formStep.id < highestStep;

                    return (
                      <button
                        type="button"
                        key={formStep.id}
                        disabled={!isAccessible}
                        onClick={async () => {
                          if (formStep.id !== step) {
                            if (formStep.id < step) {
                              setStep(formStep.id);
                            } else {
                              const errors = await validateForm();
                              const currentStepInfo = formSteps.find(
                                (s) => s.id === step,
                              );
                              const stepErrors = currentStepInfo
                                ? Object.keys(errors).filter((key) =>
                                    currentStepInfo.fields.includes(key),
                                  )
                                : [];

                              if (stepErrors.length === 0) {
                                setHighestStep((prev) =>
                                  Math.max(prev, step + 1),
                                );
                                setStep(formStep.id);
                              } else {
                                const touchedFields =
                                  currentStepInfo?.fields.reduce(
                                    (acc, field) => {
                                      acc[field] = true;
                                      return acc;
                                    },
                                    {} as Record<string, boolean>,
                                  );
                                setTouched(touchedFields || {}, true);
                              }
                            }
                          }
                        }}
                        className={`group relative flex flex-shrink-0 items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left w-auto lg:w-full
                ${isCurrent ? "bg-blumine-50 text-blumine-900 font-semibold" : "text-slate-500"}
                ${isAccessible && !isCurrent ? "hover:bg-slate-50 hover:text-slate-900 cursor-pointer" : ""}
                ${!isAccessible ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <div
                          className={`flex items-center justify-center w-6 h-6 rounded-md transition-colors
                  ${isCurrent ? "text-blumine-600" : ""}
                  ${isCompleted && !isCurrent ? "text-blumine-400" : ""}
                  ${!isCurrent && !isCompleted ? "text-slate-400 group-hover:text-slate-600" : ""}`}
                        >
                          {isCompleted && !isCurrent ? (
                            <Check className="w-4 h-4 stroke-[2.5] text-blumine-600" />
                          ) : (
                            <formStep.icon
                              className={`w-4 h-4 ${isCurrent ? "stroke-[2.5]" : "stroke-[2]"}`}
                            />
                          )}
                        </div>
                        <span
                          className={`whitespace-nowrap ${isCurrent ? "font-semibold" : ""}`}
                        >
                          {formStep.name}
                        </span>
                      </button>
                    );
                  })}
                </nav>

                {parts && parts.length > 0 && (
                  <div className="mt-4 lg:mt-6 pt-4 lg:pt-6 border-t border-slate-200 flex-1 flex flex-col min-h-0">
                    <h4 className="hidden lg:block text-[11px] font-semibold text-slate-400 mb-3 uppercase tracking-wider px-2">
                      Selected Lyrics Fragment
                    </h4>
                    <div className="overflow-y-auto custom-scrollbar pr-2 -mr-2 max-h-24 lg:max-h-full">
                      <div
                        className="text-[13px] text-zinc-700 bg-amber-50/50 p-4 rounded-lg font-mono whitespace-pre-wrap leading-relaxed border border-amber-100"
                        style={{
                          fontFamily: "'Courier Prime', 'Courier', monospace",
                        }}
                      >
                        {parts.map((p) => p.text).join(" ")}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Fields Canvas Panel */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-8 md:px-12 lg:px-16 pt-6 lg:pt-10 pb-24 lg:pb-12 custom-scrollbar bg-white">
                <div className="mx-auto">
                  {(() => {
                    const currentStepInfo = formSteps.find(
                      (s) => s.id === step,
                    );
                    return (
                      <div className="mb-8 lg:mb-10 text-left">
                        <span className="inline-block text-[11px] font-mono text-blumine-500 uppercase tracking-widest mb-2">
                          STEP {step} OF {formSteps.length}
                        </span>
                        <h2 className="text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">
                          {currentStepInfo?.name}
                        </h2>
                        <p className="text-slate-500 mt-2 text-sm leading-relaxed max-w-xl">
                          Configure the necessary parameters for this song scene
                          sequence.
                        </p>
                      </div>
                    );
                  })()}

                  <div className="space-y-8 lg:space-y-10">
                    {/* STEP 1 */}
                    {step === 1 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                          <FormikInput
                            label="Shot Number"
                            name="shot_number"
                            type="number"
                            readOnly
                            value={
                              userShotValue?.shot_number || shots.length + 1
                            }
                          />
                          <div>
                            <label className="mb-1.5 text-sm font-medium text-slate-700 capitalize flex">
                              Priority
                              <AsteriskIcon className="size-3 text-red-500" />
                            </label>
                            <div className="flex items-center p-1 bg-slate-100 rounded-full ring-1 ring-inset ring-slate-200/50">
                              <button
                                type="button"
                                onClick={() =>
                                  setFieldValue("priority", "Essential")
                                }
                                className={`w-full rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                                  values.priority === "Essential"
                                    ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/50"
                                    : "text-slate-500 hover:text-slate-900"
                                }`}
                              >
                                Essential
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setFieldValue("priority", "Optional")
                                }
                                className={`w-full rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                                  values.priority === "Optional"
                                    ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/50"
                                    : "text-slate-500 hover:text-slate-900"
                                }`}
                              >
                                Optional
                              </button>
                            </div>
                            <ErrorMessage
                              name="priority"
                              className="text-red-500 text-sm mt-1"
                              component="div"
                            />
                          </div>
                          <FormikSelect
                            label="Camera"
                            name="camera_name"
                            selectedOption="Select Camera"
                            optionData={cameras}
                            enableRedAsterick
                            value={values.camera_name}
                            onChange={(e) =>
                              setFieldValue("camera_name", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Shot Type"
                            name="shot_type"
                            selectedOption="Select Shot Type"
                            optionData={shot_types}
                            enableRedAsterick
                            value={values.shot_type}
                            onChange={(e) =>
                              setFieldValue("shot_type", e.target.value)
                            }
                          />
                        </div>
                      </section>
                    )}

                    {/* STEP 2 */}
                    {step === 2 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                          <div>
                            <FormikMultiSelect
                              name="camera_angle"
                              label="Camera Angle"
                              options={camera_angles}
                              returnObjects={true}
                              placeholder="Select Angles..."
                            />
                          </div>
                          <FormikSelect
                            label="Camera Movement"
                            name="camera_movement"
                            selectedOption="Select Movement"
                            optionData={camera_movements}
                            enableRedAsterick
                            value={values.camera_movement}
                            onChange={(e) =>
                              setFieldValue("camera_movement", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Camera Support"
                            name="camera_support"
                            selectedOption="Select Support"
                            optionData={camera_supports}
                            enableRedAsterick
                            value={values.camera_support}
                            onChange={(e) =>
                              setFieldValue("camera_support", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Lens (mm)"
                            name="lens_mm"
                            selectedOption="Select Lens"
                            optionData={lens_options}
                            valueProperty="name"
                            enableRedAsterick
                            value={values.lens_mm}
                            onChange={(e) => {
                              const lensName = e.target.value;
                              setFieldValue("lens_mm", lensName);
                              const selectedLens = lens_options.find(
                                (l) => l.name === lensName,
                              );
                              if (selectedLens) {
                                setFieldValue("lens_feel", selectedLens.type);
                              }
                            }}
                          />
                          <FormikInput
                            label="Lens Feel"
                            name="lens_feel"
                            type="text"
                            readOnly
                            placeholder="e.g. Wide, Normal, Telephoto"
                          />
                          <FormikSelect
                            label="Aperture (T-Stop)"
                            name="aperture"
                            selectedOption="Select Aperture"
                            optionData={aperture_options}
                            valueProperty="label"
                            labelProperty="label"
                            enableRedAsterick
                            value={values.aperture}
                            onChange={(e) =>
                              setFieldValue("aperture", e.target.value)
                            }
                          />
                          <FormikSelect
                            label="Frame Rate (FPS)"
                            name="fps"
                            selectedOption="Select FPS"
                            optionData={fps_options}
                            valueProperty="value"
                            labelProperty="label"
                            enableRedAsterick
                            value={values.fps}
                            onChange={(e) =>
                              setFieldValue("fps", Number(e.target.value))
                            }
                          />
                        </div>
                      </section>
                    )}

                    {/* STEP 3 */}
                    {step === 3 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                          <FormikInput
                            label="Subject"
                            name="subject"
                            type="text"
                            placeholder="e.g. Lead Singer, Backing Dancers"
                            enableRedAsterick
                          />
                          <FormikSelect
                            label="Framing"
                            name="framing"
                            selectedOption="Select Framing"
                            optionData={shot_frame}
                            value={values.framing}
                            onChange={(e) =>
                              setFieldValue("framing", e.target.value)
                            }
                          />
                          <div className="flex flex-col gap-3">
                            <FormikSelect
                              label="Depth of Field"
                              name="depth_of_field"
                              selectedOption="Select Depth of Field"
                              optionData={depth_of_field_options}
                              value={values.depth_of_field}
                              onChange={(e) =>
                                setFieldValue("depth_of_field", e.target.value)
                              }
                            />
                            {values.depth_of_field === "Other" && (
                              <FormikInput
                                label="Custom Depth of Field"
                                name="custom_depth_of_field"
                                type="text"
                                placeholder="Enter custom depth of field..."
                                className="animate-in fade-in slide-in-from-top-2 duration-300"
                              />
                            )}
                          </div>
                          <FormikInput
                            label="Duration (seconds)"
                            name="duration_seconds"
                            type="number"
                            min={0}
                            enableRedAsterick
                          />
                        </div>
                      </section>
                    )}

                    {/* STEP 4 */}
                    {step === 4 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6 lg:space-y-10">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                          <FormikMultiSelect
                            name="lighting"
                            label="Lighting"
                            options={lighting_options}
                            returnObjects={true}
                            placeholder="Select Lighting..."
                          />
                          <FormikMultiSelect
                            name="sound"
                            label="Sound"
                            options={sound_options}
                            returnObjects={true}
                            placeholder="Select Sound..."
                          />
                          <FormikSelect
                            label="VFX"
                            name="vfx"
                            selectedOption="Select VFX"
                            optionData={vfx_types}
                            value={values.vfx}
                            onChange={(e) =>
                              setFieldValue("vfx", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-6 lg:space-y-10">
                          <FormikTextarea
                            label="Visual Action Blocking"
                            name="action_blocking"
                            placeholder="Singer turns and gestures to the crowd as camera pans..."
                          />
                          <FormikTextarea
                            label="Emotional Purpose"
                            name="emotional_purpose"
                            placeholder="Builds anticipation leading into the beat drop..."
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                            <FormikSelect
                              label="Edit & Transition Purpose"
                              name="edit_purpose"
                              selectedOption="Select Edit Purpose"
                              optionData={editing_purpose}
                              value={values.edit_purpose}
                              onChange={(e) =>
                                setFieldValue("edit_purpose", e.target.value)
                              }
                            />
                            <FormikTextarea
                              label="Reason"
                              name="reason"
                              placeholder="Why is this shot necessary to visual narrative?"
                            />
                          </div>
                        </div>
                      </section>
                    )}

                    {/* STEP 5: VISUAL ASSETS & CASTING */}
                    {step === 5 && (
                      <section className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
                        {/* Interactive Character Select Array */}
                        <div className="space-y-4 text-left pb-6">
                          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                            Movie Characters featuring in this Song
                          </h3>
                          <p className="text-xs text-slate-500 leading-relaxed -mt-2">
                            Select which movie characters are highlighted in
                            this visual camera shot. These characters are pulled
                            directly from your screenplay draft.
                          </p>

                          {/* Chips list of currently selected characters */}
                          {selectedCharacters.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 p-3 bg-slate-50/50 rounded-xl border border-slate-200/40">
                              {selectedCharacters.map((char) => (
                                <div
                                  key={char.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-blumine-50/60 border border-blumine-100/60 text-blumine-700 shadow-xs"
                                >
                                  <span>{char.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleToggleCharacter(char)}
                                    className="hover:bg-blumine-100/80 text-blumine-500 hover:text-blumine-800 rounded-full p-0.5 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Movie Extracted Characters list */}
                          <div className="space-y-2">
                            <span className="text-[11px] font-mono font-bold text-slate-400 uppercase tracking-widest">
                              Screenplay Extracted Cast
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {movieCharacters.map((char) => {
                                const isSelected = !!selectedCharacters.find(
                                  (c) => c.id === char.id,
                                );
                                return (
                                  <button
                                    type="button"
                                    key={char.id}
                                    onClick={() => handleToggleCharacter(char)}
                                    className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all duration-150 active:scale-95 ${
                                      isSelected
                                        ? "bg-blumine-50/60 border-blumine-200 text-blumine-700 shadow-xs"
                                        : "bg-white hover:bg-slate-50 border-slate-200 text-slate-500"
                                    }`}
                                  >
                                    {isSelected ? "✓ " : "+ "}
                                    {char.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          {/* Selected Crowd / Group Performers chips */}
                          {values.crowd && values.crowd.length > 0 && (
                            <div className="flex flex-col gap-2 p-3 bg-slate-50/50 rounded-xl border border-slate-200/40 text-left">
                              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">
                                Added Custom Performers & Groups
                              </span>
                              <div className="flex flex-wrap gap-1.5">
                                {values.crowd.map((item: any) => (
                                  <div
                                    key={item.id}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-emerald-50/60 border border-emerald-100/60 text-emerald-800 shadow-xs"
                                  >
                                    <span className="font-bold">
                                      {item.name}
                                    </span>
                                    <span className="text-[9px] text-emerald-600 bg-emerald-100/40 px-1.5 py-0.25 rounded-sm">
                                      {item.gender}
                                    </span>
                                    <span className="text-[9px] text-emerald-600 bg-emerald-100/40 px-1.5 py-0.25 rounded-sm">
                                      {item.ageGroup}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const nextCrowd = values.crowd.filter(
                                          (c: any) => c.id !== item.id,
                                        );
                                        setFieldValue("crowd", nextCrowd);
                                      }}
                                      className="hover:bg-emerald-100/80 text-emerald-500 hover:text-emerald-800 rounded-full p-0.5 transition-colors ml-1"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Custom entries */}
                          <div className="space-y-3 pt-3 border-t border-slate-100">
                            <span className="text-[11px] font-mono font-bold text-slate-400 uppercase tracking-widest block text-left">
                              Add Custom Performer or Group
                            </span>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl text-left animate-in fade-in duration-200">
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                  Performer / Group Name
                                </label>
                                <input
                                  type="text"
                                  value={customCharacterName}
                                  onChange={(e) =>
                                    setCustomCharacterName(e.target.value)
                                  }
                                  placeholder="e.g. SINGER, CHORUS DANCERS"
                                  className="w-full px-3.5 py-1.5 text-xs rounded-xl border border-slate-200 focus:border-blumine-400 focus:outline-hidden"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleAddCustomCrowd();
                                    }
                                  }}
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                  Type (Gender)
                                </label>
                                <select
                                  value={customCrowdGender}
                                  onChange={(e) =>
                                    setCustomCrowdGender(e.target.value)
                                  }
                                  className="w-full px-3.5 py-1.5 text-xs rounded-xl border border-slate-200 focus:border-blumine-400 focus:outline-hidden bg-white"
                                >
                                  <option value="">Select Type</option>
                                  {crowdTypeOption.map((opt) => (
                                    <option key={opt.name} value={opt.name}>
                                      {opt.name}
                                    </option>
                                  ))}
                                  <option value="Mixed">Mixed</option>
                                </select>
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                  Age Group
                                </label>
                                <select
                                  value={customCrowdAgeGroup}
                                  onChange={(e) =>
                                    setCustomCrowdAgeGroup(e.target.value)
                                  }
                                  className="w-full px-3.5 py-1.5 text-xs rounded-xl border border-slate-200 focus:border-blumine-400 focus:outline-hidden bg-white"
                                >
                                  <option value="">Select Age Group</option>
                                  {crowdAgeOption.map((opt) => (
                                    <option key={opt.name} value={opt.name}>
                                      {opt.name}
                                    </option>
                                  ))}
                                  <option value="All">All</option>
                                </select>
                              </div>
                            </div>
                            <div className="flex justify-start">
                              <button
                                type="button"
                                onClick={handleAddCustomCrowd}
                                className="px-3.5 py-1.5 bg-blumine-600 hover:bg-blumine-700 text-white rounded-xl text-xs font-bold flex items-center gap-1 transition-all active:scale-95 shadow-md shadow-blumine-600/10"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add Custom Performer / Group
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Dynamic Assets Array inputs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8 pt-6 border-t border-slate-100">
                          {/* Props */}
                          <FieldArray name="props">
                            {({ push, remove }) => (
                              <div className="flex flex-col gap-3 p-5 bg-white rounded-2xl border border-slate-200 shadow-xs text-left">
                                <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider block mb-1">
                                  Props
                                </span>
                                <div className="flex flex-col gap-3">
                                  {values.props && values.props.length > 0
                                    ? values.props.map(
                                        (item: any, index: number) => (
                                          <div
                                            key={index}
                                            className="flex items-center gap-2"
                                          >
                                            <Field
                                              name={`props.${index}.name`}
                                              placeholder="Name"
                                              className="w-full px-4 py-2.5 text-sm rounded-[2.5rem] border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all placeholder:text-slate-400 text-slate-700"
                                            />
                                            <Field
                                              name={`props.${index}.quantity`}
                                              placeholder="Qty"
                                              className="w-20 shrink-0 px-4 py-2.5 text-sm text-center rounded-[2.5rem] border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all placeholder:text-slate-400 text-slate-700"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => remove(index)}
                                              className="flex-shrink-0 text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors flex items-center justify-center"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ),
                                      )
                                    : null}
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    push({ name: "", quantity: "" })
                                  }
                                  className="self-start mt-1 px-4 py-2 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all shadow-xs flex items-center gap-1.5"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add Prop
                                </button>
                              </div>
                            )}
                          </FieldArray>

                          {/* Set Props */}
                          <FieldArray name="set_props">
                            {({ push, remove }) => (
                              <div className="flex flex-col gap-3 p-5 bg-white rounded-2xl border border-slate-200 shadow-xs text-left">
                                <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider block mb-1">
                                  Set Props
                                </span>
                                <div className="flex flex-col gap-3">
                                  {values.set_props &&
                                  values.set_props.length > 0
                                    ? values.set_props.map(
                                        (item: any, index: number) => (
                                          <div
                                            key={index}
                                            className="flex items-center gap-2"
                                          >
                                            <Field
                                              name={`set_props.${index}.name`}
                                              placeholder="Name"
                                              className="w-full px-4 py-2.5 text-sm rounded-[2.5rem] border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all placeholder:text-slate-400 text-slate-700"
                                            />
                                            <Field
                                              name={`set_props.${index}.quantity`}
                                              placeholder="Qty"
                                              className="w-20 shrink-0 px-4 py-2.5 text-sm text-center rounded-[2.5rem] border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all placeholder:text-slate-400 text-slate-700"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => remove(index)}
                                              className="flex-shrink-0 text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors flex items-center justify-center"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ),
                                      )
                                    : null}
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    push({ name: "", quantity: "" })
                                  }
                                  className="self-start mt-1 px-4 py-2 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all shadow-xs flex items-center gap-1.5"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add Set Prop
                                </button>
                              </div>
                            )}
                          </FieldArray>

                          {/* Vehicles */}
                          <FieldArray name="vehicles">
                            {({ push, remove }) => (
                              <div className="flex flex-col gap-3 p-5 bg-white rounded-2xl border border-slate-200 shadow-xs text-left">
                                <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider block mb-1">
                                  Vehicles
                                </span>
                                <div className="flex flex-col gap-3">
                                  {values.vehicles && values.vehicles.length > 0
                                    ? values.vehicles.map(
                                        (item: any, index: number) => (
                                          <div
                                            key={index}
                                            className="flex items-center gap-2"
                                          >
                                            <Field
                                              name={`vehicles.${index}.name`}
                                              placeholder="Name"
                                              className="w-full px-4 py-2.5 text-sm rounded-[2.5rem] border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all placeholder:text-slate-400 text-slate-700"
                                            />
                                            <Field
                                              name={`vehicles.${index}.quantity`}
                                              placeholder="Qty"
                                              className="w-20 shrink-0 px-4 py-2.5 text-sm text-center rounded-[2.5rem] border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all placeholder:text-slate-400 text-slate-700"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => remove(index)}
                                              className="flex-shrink-0 text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors flex items-center justify-center"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ),
                                      )
                                    : null}
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    push({ name: "", quantity: "" })
                                  }
                                  className="self-start mt-1 px-4 py-2 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all shadow-xs flex items-center gap-1.5"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add Vehicle
                                </button>
                              </div>
                            )}
                          </FieldArray>

                          {/* Costumes (No Quantity) */}
                          <FieldArray name="costumes">
                            {({ push, remove }) => (
                              <div className="flex flex-col gap-3 p-5 bg-white rounded-2xl border border-slate-200 shadow-xs text-left">
                                <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider block mb-1">
                                  Costumes & Wardrobe
                                </span>
                                <div className="flex flex-col gap-3">
                                  {values.costumes && values.costumes.length > 0
                                    ? values.costumes.map(
                                        (item: any, index: number) => (
                                          <div
                                            key={index}
                                            className="flex items-center gap-2"
                                          >
                                            <Field
                                              name={`costumes.${index}.name`}
                                              placeholder="Name"
                                              className="w-full px-4 py-2.5 text-sm rounded-[2.5rem] border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all placeholder:text-slate-400 text-slate-700"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => remove(index)}
                                              className="flex-shrink-0 text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition-colors flex items-center justify-center"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ),
                                      )
                                    : null}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => push({ name: "" })}
                                  className="self-start mt-1 px-4 py-2 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all shadow-xs flex items-center gap-1.5"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add Costume
                                </button>
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Steps Footer Action Buttons */}
            <div className="sticky bottom-0 border-t border-slate-200 px-6 lg:px-12 py-4 lg:py-5 flex justify-between items-center z-10 rounded-b-xl lg:rounded-2xl bg-white">
              <div>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors rounded-md hover:bg-slate-50"
                  >
                    <span>←</span> Back
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 rounded-md hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                {step !== 5 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="bg-blumine-600 rounded-full inline-flex items-center justify-center gap-1.5 px-5 py-2 text-sm md:text-md font-medium text-white shadow-md transition-all duration-200 hover:bg-blumine-700 focus:outline-hidden cursor-pointer"
                  >
                    Next Step
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className="bg-blumine-600 rounded-full inline-flex items-center justify-center gap-1.5 px-5 py-2 text-sm md:text-md font-medium text-white shadow-md transition-all duration-200 hover:bg-blumine-700 focus:outline-hidden disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : userShotValue ? (
                      "Save Changes"
                    ) : (
                      "Create Shot"
                    )}
                  </button>
                )}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddLyricalShotForm;
